import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Loader2, MapPin, RefreshCw, Building2, Wifi, WifiOff, AlertTriangle, Globe } from 'lucide-react';
import { api } from '../contexts/AuthContext';

const statusStyles = {
  in_hotel: { bg: 'rgba(16, 185, 129, 0.10)', border: '#10b981', text: '#047857', label: 'IN STRUTTURA' },
  in_cluster: { bg: 'rgba(245, 158, 11, 0.10)', border: '#f59e0b', text: '#b45309', label: 'IN CLUSTER' },
  external: { bg: 'rgba(148, 163, 184, 0.10)', border: '#94a3b8', text: '#475569', label: 'ESTERNO' },
};

const formatRelative = (iso) => {
  if (!iso) return '—';
  try {
    const diff = (Date.now() - new Date(iso).getTime()) / 1000;
    if (diff < 60) return `${Math.floor(diff)}s fa`;
    if (diff < 3600) return `${Math.floor(diff / 60)}min fa`;
    return `${Math.floor(diff / 3600)}h fa`;
  } catch (_) { return '—'; }
};

const GeolocationPage = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [registry, setRegistry] = useState([]);
  const [showRegistry, setShowRegistry] = useState(false);

  const loadAll = useCallback(async () => {
    setRefreshing(true);
    try {
      const [techRes, regRes] = await Promise.all([
        api.get('/geolocation/technicians?window_seconds=300'),
        api.get('/geolocation/registry'),
      ]);
      setData(techRes.data);
      setRegistry(regRes.data.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
    const interval = setInterval(loadAll, 30000); // Auto refresh 30s
    return () => clearInterval(interval);
  }, [loadAll]);

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  const techs = data?.technicians || [];
  const counts = techs.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {});

  return (
    <div className="space-y-6" data-testid="geolocation-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 flex items-center gap-2">
            <MapPin className="w-7 h-7 text-red-600" /> Geolocalizzazione Tecnici
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Rilevamento automatico della posizione tramite IP pubblico rispetto ai front office. Auto-refresh ogni 30s.
          </p>
        </div>
        <Button onClick={loadAll} disabled={refreshing} variant="outline" size="sm" data-testid="geo-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} /> Aggiorna
        </Button>
      </div>

      {/* Privacy warning */}
      <div
        className="p-3 rounded-md flex items-start gap-2 text-xs"
        style={{ background: 'rgba(59, 130, 246, 0.08)', border: '1px solid rgba(59, 130, 246, 0.3)', color: '#1e40af' }}
      >
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <div>
          <b>Nota privacy:</b> questo sistema rileva la posizione dei tecnici tramite correlazione IP con i front office (Art. 4 Statuto Lavoratori). Assicurati che sia coperto dall&apos;informativa GDPR aziendale.
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card data-testid="kpi-online"><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Tecnici Online (5min)</div><div className="text-3xl font-extrabold text-slate-900">{techs.length}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">In struttura</div><div className="text-3xl font-extrabold text-emerald-600">{counts.in_hotel || 0}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">In cluster</div><div className="text-3xl font-extrabold text-amber-600">{counts.in_cluster || 0}</div></CardContent></Card>
        <Card><CardContent className="pt-4"><div className="text-[10px] uppercase font-bold text-slate-500">Esterni / transito</div><div className="text-3xl font-extrabold text-slate-500">{counts.external || 0}</div></CardContent></Card>
      </div>

      {/* Registry status */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center justify-between">
            <span className="flex items-center gap-2"><Globe className="w-4 h-4 text-blue-600" /> Mappa IP appresi dai Front Office</span>
            <Button variant="ghost" size="sm" onClick={() => setShowRegistry(!showRegistry)}>
              {showRegistry ? 'Nascondi' : `Mostra (${registry.length})`}
            </Button>
          </CardTitle>
        </CardHeader>
        {showRegistry && (
          <CardContent>
            {registry.length === 0 ? (
              <p className="text-sm text-slate-500">Nessun IP appreso ancora. Serve che i Front Office facciano ping dall&apos;IP fisso dell&apos;hotel per almeno 30 giorni.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs" data-testid="registry-list">
                {registry.map(r => (
                  <div key={r.hotel_id} className="flex justify-between items-center border rounded p-2 bg-slate-50 dark:bg-slate-800/40">
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900 dark:text-slate-100 truncate">{r.hotel_name}</div>
                      <div className="font-mono text-slate-500">{r.primary_ip} · subnet {r.subnets.join(', ')}</div>
                    </div>
                    <Badge variant={r.confidence === 'alta' ? 'default' : 'secondary'} className="text-[10px]">{r.confidence}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Techs list */}
      <Card>
        <CardHeader><CardTitle className="text-lg font-bold">Tecnici attualmente online</CardTitle></CardHeader>
        <CardContent>
          {techs.length === 0 ? (
            <div className="text-center py-10 text-slate-500">
              <WifiOff className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-sm">Nessun tecnico attivo negli ultimi 5 minuti</p>
              {data?.registry_size === 0 && (
                <p className="text-xs text-amber-600 mt-2">⚠ Mappa IP vuota — serve che i FO usino l&apos;app per almeno qualche giorno prima di poter geolocalizzare.</p>
              )}
            </div>
          ) : (
            <div className="space-y-2" data-testid="techs-list">
              {techs.map(t => {
                const s = statusStyles[t.status] || statusStyles.external;
                return (
                  <div
                    key={t.user_id}
                    className="flex items-center gap-3 p-3 rounded-lg border-l-4"
                    style={{ background: s.bg, borderLeftColor: s.border }}
                    data-testid={`tech-row-${t.user_id}`}
                  >
                    <Wifi className="w-5 h-5 shrink-0" style={{ color: s.border }} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-bold text-slate-900 truncate">{t.name}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-bold text-white" style={{ background: s.border }}>
                          {s.label}
                        </span>
                        {t.hotel_name && <Badge variant="secondary" className="text-[10px] flex items-center gap-1"><Building2 className="w-3 h-3" />{t.hotel_name}</Badge>}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-500 flex-wrap">
                        <span className="font-mono">{t.ip || '—'}</span>
                        {t.device && <span>· {t.device}</span>}
                        <span>· visto {formatRelative(t.last_seen)}</span>
                      </div>
                      {t.status === 'in_cluster' && t.cluster_hotel_names.length > 0 && (
                        <div className="mt-1 text-[11px]" style={{ color: s.text }}>
                          <b>Probabilmente in uno di:</b> {t.cluster_hotel_names.join(', ')}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GeolocationPage;
